import { gql } from "@apollo/client";
import { Intent } from "@blueprintjs/core";
import {
  OrgId,
  OrgRole,
  OrgRoleRequestGatedAction,
  OrgRoleRequestSource,
  humanReadableOrgRole,
  sentenceCase,
} from "@hex/common";
import React, { useCallback, useEffect } from "react";

import { HexButton } from "../../hex-components/HexButton.js";
import { useCurrentUser } from "../../hooks/me/useCurrentUser.js";
import {
  LocalStorageKeys,
  useLocalStorage,
} from "../../hooks/useLocalStorage.js";
import { ORG_ID } from "../../orgs.js";
import { ThemeOverrideWrapper } from "../../ThemeWrapper.js";
import { useDialog } from "../../util/dialogs.js";
import { ContactAnAdmin } from "../common/ContactAnAdmin.js";
import { SendIcon } from "../icons/CustomIcons.js";
import { OrgRequestAccessDialog } from "../share/RequestAccessDialog.js";

import {
  useGetOrgDirectorySyncEnabledQuery,
  useRequestOrgRoleMutation,
} from "./OrgRoleRequestButton.generated.js";

gql`
  mutation RequestOrgRole(
    $gatedAction: OrgRoleRequestGatedAction
    $requestedOrgRole: OrgRole!
    $reason: String
    $requestSource: OrgRoleRequestSource
  ) {
    requestOrgRole(
      gatedAction: $gatedAction
      requestedOrgRole: $requestedOrgRole
      reason: $reason
      toExplore: false
      requestSource: $requestSource
    )
  }
`;

gql`
  query GetOrgDirectorySyncEnabled($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      directorySyncEnabled
    }
  }
`;

interface OrgRoleRequestButtonProps {
  className?: "string";
  /** Default role name is "Editor", but there are places where we want to
   * clarify "workspace Editor"
   */
  role?: OrgRole;
  small?: boolean;
  roleNameOverride?: string;
  /**
   * For event tracking: the action that the user is blocked from performing and
   * the location of the UI that is inaccessible
   */
  gatedAction: OrgRoleRequestGatedAction;
  requestSource: OrgRoleRequestSource;
}
export const OrgRoleRequestButton: React.ComponentType<OrgRoleRequestButtonProps> =
  React.memo(function OrgRoleRequestButton({
    className,
    gatedAction,
    requestSource,
    role = OrgRole.EDITOR,
    roleNameOverride,
    small = true,
  }) {
    const currentUser = useCurrentUser();
    const { data } = useGetOrgDirectorySyncEnabledQuery({
      variables: { orgId: ORG_ID },
    });

    const directorySyncEnabled = data?.orgById.directorySyncEnabled ?? false;
    const [orgRoleRequested, setOrgRoleRequested] = useLocalStorage(
      LocalStorageKeys.ORG_ROLE_REQUESTED({
        orgId: currentUser?.org.id ?? ("unknown" as OrgId),
        requestedOrgRole: role,
      }),
    );

    const [requestOrgRoleMutation] = useRequestOrgRoleMutation();

    const requestOrgRole = useCallback(
      async (requestedOrgRole: OrgRole, reason: string | null) => {
        setOrgRoleRequested(true);
        await requestOrgRoleMutation({
          variables: {
            requestedOrgRole,
            reason,
            gatedAction,
            requestSource,
          },
        });
      },
      [setOrgRoleRequested, requestOrgRoleMutation, gatedAction, requestSource],
    );

    const roleName = roleNameOverride ?? humanReadableOrgRole(role);

    const { closeDialog, isOpen, openDialog } = useDialog("request-org-access");

    const openRequestAccessDialog = useCallback(
      () => openDialog(),
      [openDialog],
    );

    useEffect(() => {
      return () => {
        // we want to clear out the dialog name from the url when this component
        // unmounts
        if (isOpen) {
          closeDialog();
        }
      };
    }, [closeDialog, isOpen]);

    // we don't want to show workspace editor request button if directory sync
    // is enabled, return contact admin CTA instead
    if (directorySyncEnabled && role === OrgRole.EDITOR) {
      return (
        <>
          <ContactAnAdmin /> to request {roleName} access
        </>
      );
    }

    const buttonText =
      gatedAction === OrgRoleRequestGatedAction.EXPLORE
        ? orgRoleRequested
          ? "Role requested"
          : "Request role"
        : orgRoleRequested
          ? `${sentenceCase(roleName)} role requested`
          : `Request ${roleName} role`;

    return (
      <>
        <HexButton
          className={className}
          disabled={orgRoleRequested}
          icon={<SendIcon />}
          intent={Intent.PRIMARY}
          small={small}
          text={buttonText}
          onClick={openRequestAccessDialog}
        />
        {/* This component is used within other components
            that could override the theme (ex. tooltips) but
            we want the dialog to just reflect the app wide theme.*/}
        <ThemeOverrideWrapper theme="base">
          <OrgRequestAccessDialog
            orgRole={role}
            requestAccessCallback={requestOrgRole}
          />
        </ThemeOverrideWrapper>
      </>
    );
  });
